import { useState } from 'react'
import { useNavigate } from 'react-router'
import { usePerfilCliente, usePerfilPorTelefono, useAlbaranesMas, type AlbaranDetalle } from '../hooks/usePerfilCliente'
import { useNotasCliente, useCrearNota, useBorrarNota, useBajaCliente, useReactivarCliente, useFijarAvisar, useAplazarDias } from '../hooks/useClientes'
import { useGeocodificar } from '../paginas/logistica/consultas'
import { DireccionesEntregaCliente } from './DireccionesEntregaCliente'
import { MapaCliente } from '../paginas/logistica/MapaCliente'
import { Cargando } from './Cargando'
import { EstadoError } from './EstadoError'
import {
  IconoCerrar,
  IconoConversaciones,
  IconoCalendario,
  IconoUbicacion,
  IconoTelefono,
  IconoFactura,
  IconoWhatsapp,
} from './iconos'
import { colorAvatar, iniciales } from '../paginas/conversaciones/avatar'

interface Props {
  /** contactId del cliente a mostrar; null = cerrado. */
  contactId: string | null
  /** Alternativa: abrir por teléfono (chats de cliente en Conversaciones). */
  telefono?: string | null
  onCerrar: () => void
}

const ESTADO_CHIP: Record<string, string> = {
  agendado: 'bg-positivo-fondo text-positivo-tinta',
  aplazado: 'bg-neutro-fondo text-neutro-tinta',
  pendiente: 'bg-pendiente-fondo text-pendiente-tinta',
  baja: 'bg-negativo-fondo text-negativo-tinta',
}

/**
 * Teléfono → enlace wa.me (abre el chat de WhatsApp del cliente). Deja solo
 * dígitos; si viene sin prefijo de país (9 dígitos, caso España) antepone 34.
 * Devuelve null si no hay un número usable.
 */
export function enlaceWaMe(telefono?: string | null): string | null {
  if (!telefono) return null
  let d = telefono.replace(/\D/g, '')
  if (d.length === 9) d = `34${d}` // número nacional español sin prefijo
  if (d.length < 8) return null
  return `https://wa.me/${d}`
}

/**
 * Perfil 360º de cliente: panel modal que se abre desde cualquier pantalla
 * (Confirmar, Logística, Avisos, un chat). Reúne ficha + ubicación en mapa +
 * estado del ciclo + historial de repartos desplegable + si respondió el
 * WhatsApp esta semana, y enlaza al chat / calendario / llamar. Solo iconos.
 *
 * Se abre por contactId (lo normal) o por teléfono (chats de cliente, donde no
 * se conoce el contactId). Exactamente uno de los dos debe venir informado.
 */
export function PerfilCliente({ contactId, telefono, onCerrar }: Props) {
  const navegar = useNavigate()
  const porId = usePerfilCliente(contactId)
  const porTel = usePerfilPorTelefono(contactId ? null : (telefono ?? null))
  const { data: p, isPending, isError, error, refetch } = contactId ? porId : porTel

  if (!contactId && !telefono) return null

  const irAlChat = () => {
    if (p?.enlaceChat) navegar(`/conversaciones?tipo=clientes&buscar=${encodeURIComponent(p.enlaceChat)}`)
  }
  const irAlCalendario = () => navegar(`/calendario?cliente=${encodeURIComponent(p?.nombre ?? '')}`)

  const estadoClase = p?.estadoCiclo ? (ESTADO_CHIP[p.estadoCiclo] ?? 'bg-neutro-fondo text-neutro-tinta') : ''

  return (
    <>
      <button
        type="button"
        aria-label="Cerrar perfil"
        onClick={onCerrar}
        className="fixed inset-0 z-40 bg-black/40"
      />
      <aside
        aria-label={`Perfil de ${p?.nombre ?? 'cliente'}`}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-hidden border-l border-borde bg-superficie shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-borde px-5 py-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">Perfil de cliente</h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="flex size-8 items-center justify-center rounded-lg text-tinta-apagada transition-colors hover:bg-elevado"
          >
            <IconoCerrar className="size-5" />
          </button>
        </header>

        {isPending ? (
          <div className="p-6"><Cargando mensaje="Cargando el perfil" /></div>
        ) : isError ? (
          <div className="p-6"><EstadoError error={error} onReintentar={() => void refetch()} /></div>
        ) : p ? (
          <div className="flex flex-col gap-5 overflow-y-auto p-5">
            {/* Cabecera */}
            <div className="flex items-center gap-4">
              <span
                className="flex size-14 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
                style={{ backgroundColor: colorAvatar(p.nombre || p.contactId) }}
              >
                {iniciales(p.nombre || p.contactId)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-tinta">{p.nombre}</p>
                <p className="flex items-center gap-1.5 text-sm text-tinta-apagada">
                  {p.telefono ? (
                    <>
                      <IconoTelefono className="size-3.5" />
                      {p.telefono}
                    </>
                  ) : (
                    'Sin teléfono'
                  )}
                  {p.poblacion ? <span>· {p.poblacion}</span> : null}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.estadoCiclo ? (
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${estadoClase}`}>
                      {p.estadoCiclo}
                    </span>
                  ) : null}
                  {p.etiquetas.slice(0, 4).map((e) => (
                    <span
                      key={e}
                      className="rounded-full bg-neutro-fondo px-2.5 py-0.5 text-[11px] font-semibold text-neutro-tinta"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Ubicación en mapa (reutiliza el MapaCliente de Logística, que ya
                resuelve CSS/iconos de Leaflet y ofrece geocodificar si faltan
                las coordenadas). */}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
                <IconoUbicacion className="size-3.5" />
                Ubicación
              </p>
              <MapaUbicacion contactId={p.contactId} nombre={p.nombre} latitud={p.latitud} longitud={p.longitud} />
            </div>

            {/* Ficha */}
            <dl className="flex flex-col gap-1.5 rounded-xl border border-borde p-4 text-[13px]">
              <Fila k="Dirección" v={p.direccion} />
              <Fila k="CP / Población" v={[p.codigoPostal, p.poblacion].filter(Boolean).join(' · ')} />
              <Fila k="Provincia" v={p.provincia} />
              <Fila k="Aplazamientos seguidos" v={String(p.aplazamientos)} />
              <Fila
                k="Esta semana (WhatsApp)"
                v={p.respondioSemana ? (p.respuestaSemana ?? 'respondió') : 'No respondió'}
                destacar={!p.respondioSemana}
              />
            </dl>

            {/* Acciones (sin "En logística": sacaba del perfil a la lista) */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Acciones</p>
              <div className="flex flex-wrap gap-2">
                <Accion icono={<IconoConversaciones className="size-4" />} texto="Ver su chat" onClick={irAlChat} principal disabled={!p.enlaceChat} />
                <Accion icono={<IconoCalendario className="size-4" />} texto="En calendario" onClick={irAlCalendario} />
                {enlaceWaMe(p.telefono) ? (
                  <a
                    href={enlaceWaMe(p.telefono) as string}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
                  >
                    <IconoWhatsapp className="size-4" />
                    Ver en WhatsApp
                  </a>
                ) : null}
                {p.telefono ? (
                  <a
                    href={`tel:${p.telefono}`}
                    className="flex items-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
                  >
                    <IconoTelefono className="size-4" />
                    Llamar
                  </a>
                ) : null}
              </div>
            </div>

            {/* Historial de repartos desplegable (líneas + total por albarán) */}
            <HistorialRepartos contactId={p.contactId} albaranesIniciales={p.albaranes ?? []} />

            {/* Direcciones de entrega (multi-reparto): varias entregas, misma facturación */}
            <DireccionesEntregaCliente
              contactId={p.contactId}
              direccionPrincipal={{
                direccion: p.direccion,
                codigoPostal: p.codigoPostal,
                ciudad: p.poblacion,
                provincia: p.provincia,
              }}
            />

            {/* Notas internas (se escriben en Holded + caché del panel) */}
            <NotasCliente contactId={p.contactId} />

            {/* Avisos de reparto: interruptor "no avisar" + aplazar N días (Iván oct-2026) */}
            <SeccionAvisos contactId={p.contactId} avisar={p.avisar !== false} aplazadoHasta={p.aplazadoHasta} />

            {/* Baja / reactivación (A.1.1): escribe el tag 'baja' en Holded */}
            <AccionBaja contactId={p.contactId} activo={p.activo} />
          </div>
        ) : null}
      </aside>
    </>
  )
}

/** Mapa de ubicación: reutiliza MapaCliente con geocodificado bajo demanda. */
function MapaUbicacion({
  contactId,
  nombre,
  latitud,
  longitud,
}: {
  contactId: string
  nombre: string
  latitud: number | null
  longitud: number | null
}) {
  const geocodificar = useGeocodificar(contactId)
  // Tras geocodificar, la mutación devuelve las coords nuevas; se usan al vuelo.
  const lat = geocodificar.data?.latitud ?? latitud
  const lon = geocodificar.data?.longitud ?? longitud
  return (
    <MapaCliente
      nombre={nombre}
      latitud={lat}
      longitud={lon}
      onGeocodificar={() => geocodificar.mutate()}
      geocodificando={geocodificar.isPending}
      error={geocodificar.error}
    />
  )
}

/** Historial de repartos: cada albarán es un desplegable con su detalle. */
function HistorialRepartos({ contactId, albaranesIniciales }: { contactId: string; albaranesIniciales: AlbaranDetalle[] }) {
  const [offset, setOffset] = useState(0)
  const mas = useAlbaranesMas(contactId, offset, offset > 0)
  // Acumula: los iniciales del perfil + lo que traiga cada "Cargar más".
  const extra = mas.data?.items ?? []
  const todos = [...albaranesIniciales, ...extra]

  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
        <IconoFactura className="size-3.5" />
        Historial de repartos
      </p>
      {todos.length === 0 ? (
        <p className="text-xs text-tinta-apagada">Sin repartos registrados (o Holded no responde en dev).</p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {todos.map((a, i) => (
              <Reparto key={`${a.fecha}-${i}`} albaran={a} ultimo={i === 0} abierto={i === 0} />
            ))}
          </div>
          {/* Si la última tanda trajo 5, puede haber más → ofrecer cargar. */}
          {(extra.length === 5 || (offset === 0 && albaranesIniciales.length === 5)) ? (
            <button
              type="button"
              disabled={mas.isFetching}
              onClick={() => setOffset((o) => (o === 0 ? albaranesIniciales.length : o + 5))}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:opacity-50"
            >
              {mas.isFetching ? 'Cargando…' : 'Cargar más albaranes'}
            </button>
          ) : null}
          {mas.isError ? (
            <p className="mt-2 text-xs text-critico-tinta">No se pudieron cargar más albaranes.</p>
          ) : null}
        </>
      )}
    </div>
  )
}

/** Un reparto desplegable: resumen en la cabecera, detalle (líneas) al abrir. */
function Reparto({ albaran, ultimo, abierto }: { albaran: AlbaranDetalle; ultimo: boolean; abierto: boolean }) {
  const resumen =
    albaran.lineas.length > 0
      ? albaran.lineas.map((l) => `${l.unidades}× ${l.nombre}`).slice(0, 1).join('')
      : 'Reparto'
  return (
    <details open={abierto} className="overflow-hidden rounded-lg border border-borde">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 [&::-webkit-details-marker]:hidden">
        <span className="min-w-[88px] text-xs text-tinta-apagada">
          {formatoFecha(albaran.fecha)}
          {ultimo ? (
            <span className="ml-1.5 rounded-full bg-positivo-fondo px-1.5 py-0.5 text-[9px] font-bold text-positivo-tinta">
              ÚLTIMO
            </span>
          ) : null}
        </span>
        <span className="flex-1 truncate text-right text-[13px] text-tinta">{resumen}</span>
      </summary>
      <div className="border-t border-dashed border-borde-suave px-3 py-2 text-[12.5px]">
        {albaran.lineas.length === 0 ? (
          <p className="text-tinta-apagada">Sin líneas en este albarán.</p>
        ) : (
          <>
            {albaran.lineas.map((l, i) => (
              <div key={i} className="flex justify-between py-0.5 text-tinta-2">
                <span>{l.unidades}× {l.nombre}</span>
                <span>{l.precio > 0 ? `${(l.precio * l.unidades).toFixed(2)} €` : '—'}</span>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t border-dashed border-borde-suave pt-1 font-semibold text-tinta">
              <span>Total</span>
              <span>{albaran.total.toFixed(2)} €</span>
            </div>
          </>
        )}
      </div>
    </details>
  )
}

function Fila({ k, v, destacar = false }: { k: string; v?: string | null; destacar?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-dashed border-borde-suave py-1 last:border-0">
      <dt className="text-tinta-apagada">{k}</dt>
      <dd className={`text-right ${destacar ? 'font-semibold text-pendiente-tinta' : 'text-tinta'}`}>{v || '—'}</dd>
    </div>
  )
}

function Accion({
  icono,
  texto,
  onClick,
  principal = false,
  disabled = false,
}: {
  icono: React.ReactNode
  texto: string
  onClick: () => void
  principal?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
        principal
          ? 'bg-acento text-white hover:opacity-90'
          : 'border border-borde-fuerte bg-superficie text-tinta-2 hover:bg-elevado',
      ].join(' ')}
    >
      {icono}
      {texto}
    </button>
  )
}

function formatoFecha(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Notas internas del cliente: se escriben en Holded (CRM) + caché del panel. */
function NotasCliente({ contactId }: { contactId: string }) {
  const { data, isPending } = useNotasCliente(contactId)
  const crear = useCrearNota(contactId)
  const borrar = useBorrarNota(contactId)
  const [texto, setTexto] = useState('')

  const notas = data?.items ?? []

  const guardar = async () => {
    const t = texto.trim()
    if (!t) return
    await crear.mutateAsync({ texto: t })
    setTexto('')
  }

  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Notas</p>
      <div className="flex flex-col gap-2">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe una nota… (se guarda también en Holded)"
          rows={2}
          className="w-full resize-none rounded-lg border border-borde bg-lienzo px-3 py-2 text-[13px] text-tinta outline-none placeholder:text-tinta-apagada focus:border-acento"
        />
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={crear.isPending || !texto.trim()}
          className="self-start rounded-lg bg-acento px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {crear.isPending ? 'Guardando…' : 'Guardar nota'}
        </button>
      </div>

      {isPending ? (
        <p className="mt-3 text-xs text-tinta-apagada">Cargando notas…</p>
      ) : notas.length === 0 ? (
        <p className="mt-3 text-xs text-tinta-apagada">Sin notas todavía.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {notas.map((n) => (
            <li key={n.id} className="rounded-lg border border-borde bg-lienzo px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <p className="whitespace-pre-wrap break-words text-[13px] text-tinta">{n.texto}</p>
                <button
                  type="button"
                  onClick={() => void borrar.mutate({ notaId: n.id })}
                  aria-label="Borrar nota"
                  className="shrink-0 text-tinta-apagada transition-colors hover:text-negativo-tinta"
                >
                  <IconoCerrar className="size-4" />
                </button>
              </div>
              <p className="mt-1 flex items-center gap-2 text-[11px] text-tinta-apagada">
                <span>{formatoFechaHora(n.creadaEn)}</span>
                {n.enHolded ? (
                  <span className="rounded-full bg-positivo-fondo px-1.5 py-0.5 font-semibold text-positivo-tinta">
                    En Holded
                  </span>
                ) : (
                  <span className="rounded-full bg-aviso-fondo px-1.5 py-0.5 font-semibold text-aviso-tinta">
                    Solo panel
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Avisos de reparto (Iván oct-2026): interruptor "no avisar" (para clientes no
 * automatizados / que recogen ellos) + aplazar el próximo aviso 7/14/21/28 días
 * (resuelve el baile de la S cuando ya se repartió). NO toca albaranes.
 */
function SeccionAvisos({ contactId, avisar, aplazadoHasta }: { contactId: string; avisar: boolean; aplazadoHasta: string | null }) {
  const fijarAvisar = useFijarAvisar()
  const aplazar = useAplazarDias()
  const hasta = aplazadoHasta ? new Date(aplazadoHasta) : null
  const aplazadoVigente = hasta != null && hasta.getTime() > Date.now()
  return (
    <div className="border-t border-borde pt-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Avisos de reparto</p>

      {/* Interruptor: ¿recibe el aviso del lunes? */}
      <div className="flex items-center gap-3 rounded-xl border border-borde p-3">
        <button
          type="button"
          role="switch"
          aria-checked={avisar}
          aria-label="Enviar aviso de reparto"
          disabled={fijarAvisar.isPending}
          onClick={() => fijarAvisar.mutate({ contactId, avisar: !avisar })}
          className={[
            'relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50',
            avisar ? 'bg-positivo-anillo' : 'bg-borde-fuerte',
          ].join(' ')}
        >
          <span className={['absolute top-0.5 size-5 rounded-full bg-white transition-all', avisar ? 'left-[22px]' : 'left-0.5'].join(' ')} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-tinta">Enviar aviso de reparto cada semana</p>
          <p className="text-[11px] text-tinta-apagada">Apágalo para clientes que recogen ellos o piden a mano</p>
        </div>
        <span className={['shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold', avisar ? 'bg-positivo-fondo text-positivo-tinta' : 'bg-negativo-fondo text-negativo-tinta'].join(' ')}>
          {avisar ? 'Recibe avisos' : 'No se le avisa'}
        </span>
      </div>

      {/* Aplazar N días */}
      <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wider text-tinta-apagada">Aplazar próximo aviso</p>
      {aplazadoVigente ? (
        <p className="mb-2 rounded-lg bg-aviso-fondo px-3 py-1.5 text-[12px] text-aviso-tinta">
          Aplazado hasta el {hasta!.toLocaleDateString('es-ES')}. No se le avisará hasta entonces.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {[7, 14, 21, 28].map((d) => (
          <button
            key={d}
            type="button"
            disabled={aplazar.isPending}
            onClick={() => aplazar.mutate({ contactId, dias: d })}
            className="rounded-full border border-borde-fuerte bg-superficie px-3.5 py-1.5 text-[13px] font-semibold text-tinta-2 transition-colors hover:border-acento hover:text-acento disabled:opacity-50"
          >
            {d} días
          </button>
        ))}
      </div>
    </div>
  )
}

/** Baja / reactivación del cliente (escribe/quita el tag 'baja' en Holded). */
function AccionBaja({ contactId, activo }: { contactId: string; activo: boolean }) {
  const baja = useBajaCliente()
  const reactivar = useReactivarCliente()
  const [confirmando, setConfirmando] = useState(false)

  if (!activo) {
    return (
      <div className="border-t border-borde pt-4">
        <button
          type="button"
          onClick={() => reactivar.mutate({ contactId })}
          disabled={reactivar.isPending}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-positivo-anillo bg-positivo-fondo px-3 py-2 text-sm font-semibold text-positivo-tinta transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {reactivar.isPending ? 'Reactivando…' : 'Reactivar cliente'}
        </button>
      </div>
    )
  }

  return (
    <div className="border-t border-borde pt-4">
      {confirmando ? (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] text-tinta">¿Dar de baja a este cliente? Se marcará con el tag <b>baja</b> en Holded y saldrá del ciclo.</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => baja.mutate({ contactId })}
              disabled={baja.isPending}
              className="flex-1 rounded-lg bg-negativo-tinta px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {baja.isPending ? 'Dando de baja…' : 'Sí, dar de baja'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmando(false)}
              className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 hover:bg-elevado"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-negativo-anillo bg-negativo-fondo px-3 py-2 text-sm font-semibold text-negativo-tinta transition-opacity hover:opacity-90"
        >
          Dar de baja
        </button>
      )}
    </div>
  )
}

function formatoFechaHora(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}
